/**
 * Variant Selection UX Enhancer
 * Provides clear feedback when users try to add to cart without selecting required variants
 * Reduces conversion friction by guiding users to complete their selection
 */

if (!customElements.get('variant-selection-enhancer')) {
  class VariantSelectionEnhancer extends HTMLElement {
    constructor() {
      super();
      this.productForm = null;
      this.submitButton = null;
      this.variantSelects = null;
      this.errorShown = false;
    }

    connectedCallback() {
      this.productForm = this.closest('product-form') || document.querySelector('product-form');
      if (!this.productForm) return;

      this.submitButton = this.productForm.querySelector('.product-form__submit');
      const productInfo = this.closest('product-info') || this.productForm.closest('product-info') || document.querySelector('product-info');
      this.variantSelects = productInfo?.querySelector('variant-selects') || this.productForm.querySelector('variant-selects');

      if (!this.submitButton) return;

      // Intercept form submission
      this.setupSubmitInterception();

      // Add real-time validation feedback
      if (this.variantSelects) {
        this.setupRealtimeValidation();
      }
    }

    setupSubmitInterception() {
      const form = this.productForm.querySelector('form');
      if (!form) return;

      form.addEventListener('submit', (e) => {
        // Check if a variant is properly selected
        const variantIdInput = form.querySelector('.product-variant-id');
        const selectedVariantId = variantIdInput?.value;

        // If no variant selected or variant is invalid
        if (!selectedVariantId || selectedVariantId === 'null' || selectedVariantId === '') {
          e.preventDefault();
          e.stopPropagation();
          this.showVariantSelectionError();
          return false;
        }

        // Check availability against the selected variant, not a stale button state.
        const variantData = this.variantSelects?.variantData || [];
        const currentVariant = variantData.find((variant) => String(variant.id) === String(selectedVariantId));
        if (currentVariant && !currentVariant.available) {
          e.preventDefault();
          e.stopPropagation();
          this.showSoldOutError();
          return false;
        }
      });
    }

    setupRealtimeValidation() {
      // Watch for variant changes
      const variantInputs = this.variantSelects.querySelectorAll('input[type="radio"], select');

      variantInputs.forEach(input => {
        input.addEventListener('change', () => {
          // Clear error when user makes a selection
          this.clearErrors();
          this.errorShown = false;
        });
      });
    }

    showVariantSelectionError() {
      if (this.errorShown) return; // Don't show multiple times

      const errorWrapper = this.productForm.querySelector('.product-form__error-message-wrapper');
      const errorMessage = this.productForm.querySelector('.product-form__error-message');

      if (errorWrapper && errorMessage) {
        errorMessage.textContent = 'Please select all options before adding to cart.';
        errorWrapper.hidden = false;
        errorWrapper.setAttribute('aria-live', 'assertive');
        errorWrapper.setAttribute('role', 'alert');

        // Scroll to first unselected option
        this.scrollToFirstUnselected();

        // Highlight unselected options
        this.highlightUnselectedOptions();

        this.errorShown = true;

        // Add shake animation
        errorWrapper.style.animation = 'shake 0.5s ease-in-out';
        setTimeout(() => {
          errorWrapper.style.animation = '';
        }, 500);
      }
    }

    showSoldOutError() {
      const errorWrapper = this.productForm.querySelector('.product-form__error-message-wrapper');
      const errorMessage = this.productForm.querySelector('.product-form__error-message');

      if (errorWrapper && errorMessage) {
        errorMessage.textContent = 'This variant is currently sold out. Please select a different option.';
        errorWrapper.hidden = false;
        errorWrapper.setAttribute('aria-live', 'assertive');
        errorWrapper.setAttribute('role', 'alert');

        // Add shake animation
        errorWrapper.style.animation = 'shake 0.5s ease-in-out';
        setTimeout(() => {
          errorWrapper.style.animation = '';
        }, 500);
      }
    }

    clearErrors() {
      const errorWrapper = this.productForm.querySelector('.product-form__error-message-wrapper');
      if (errorWrapper) {
        errorWrapper.hidden = true;
        errorWrapper.removeAttribute('aria-live');
        errorWrapper.removeAttribute('role');
      }

      // Remove highlight from options
      if (this.variantSelects) {
        const fieldsets = this.variantSelects.querySelectorAll('.product-form__input');
        fieldsets.forEach(fieldset => {
          fieldset.style.border = '';
          fieldset.style.boxShadow = '';
        });
      }
    }

    scrollToFirstUnselected() {
      if (!this.variantSelects) return;

      const selects = this.variantSelects.querySelectorAll('select');
      const radioGroups = this.variantSelects.querySelectorAll('fieldset');

      // Check select dropdowns
      for (const select of selects) {
        if (!select.value || select.value === '') {
          select.scrollIntoView({ behavior: 'smooth', block: 'center' });
          select.focus();
          return;
        }
      }

      // Check radio button groups
      for (const fieldset of radioGroups) {
        const checkedRadio = fieldset.querySelector('input[type="radio"]:checked');
        if (!checkedRadio) {
          fieldset.scrollIntoView({ behavior: 'smooth', block: 'center' });
          const firstRadio = fieldset.querySelector('input[type="radio"]');
          if (firstRadio) firstRadio.focus();
          return;
        }
      }
    }

    highlightUnselectedOptions() {
      if (!this.variantSelects) return;

      const fieldsets = this.variantSelects.querySelectorAll('.product-form__input');

      fieldsets.forEach(fieldset => {
        const select = fieldset.querySelector('select');
        const checkedRadio = fieldset.querySelector('input[type="radio"]:checked');

        // If this is a select dropdown and has no value, highlight it
        if (select && (!select.value || select.value === '')) {
          fieldset.style.border = '2px solid rgba(217, 45, 32, 0.6)';
          fieldset.style.boxShadow = '0 0 0 3px rgba(217, 45, 32, 0.1)';
          fieldset.style.borderRadius = '0.4rem';
          fieldset.style.padding = '1rem';
          fieldset.style.transition = 'all 0.3s ease';
        }

        // If this is a radio group and none are checked, highlight it
        if (!select && !checkedRadio) {
          fieldset.style.border = '2px solid rgba(217, 45, 32, 0.6)';
          fieldset.style.boxShadow = '0 0 0 3px rgba(217, 45, 32, 0.1)';
          fieldset.style.borderRadius = '0.4rem';
          fieldset.style.padding = '1rem';
          fieldset.style.transition = 'all 0.3s ease';
        }
      });
    }
  }

  customElements.define('variant-selection-enhancer', VariantSelectionEnhancer);
}
